import { buildDefaultFeaturesFromConfig } from '../constants/feature-flags.constants';

/**
 * Feature Flags Interface
 * All feature flags are boolean-only (ON/OFF toggles)
 * Add properties here for IDE autocomplete. Runtime structure from FEATURE_FLAGS_CONFIG.
 * Index signature required for dynamic effects - do not remove.
 */
export interface FeatureFlags {
  payroll: {
    useV2ApiForAutomation: boolean;
  };
  diagnostics: {
    enableDiagnosticPageAccess: boolean;
  };
  [key: string]: Record<string, boolean>;
}

/**
 * Create default feature flags from constants configuration
 */
export const createDefaultFeatures = (): FeatureFlags => {
  return buildDefaultFeaturesFromConfig() as FeatureFlags;
};

/**
 * Validate that an object has the expected feature flags structure
 */
export function isValidFeatureFlags(
  obj: Record<string, Record<string, unknown>>
): obj is FeatureFlags {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  // Check that all values are objects (domains contain properties)
  for (const domain of Object.keys(obj)) {
    const domainValue = obj[domain];
    if (
      typeof domainValue !== 'object' ||
      domainValue === null ||
      Array.isArray(domainValue)
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Convert a dynamic flags object to FeatureFlags type with safe defaults
 * Deep merges incoming object with auto-generated defaults
 */
export function toFeatureFlags(
  obj: Record<string, Record<string, unknown>>
): FeatureFlags {
  const defaults = createDefaultFeatures();

  // If structure is invalid, return defaults
  if (!isValidFeatureFlags(obj)) {
    console.warn(
      '[FeatureFlags] Invalid structure received, using defaults:',
      obj
    );
    return defaults;
  }

  // Deep merge: Combine incoming flags with defaults
  const merged: Record<string, Record<string, unknown>> = { ...defaults };

  for (const domain of Object.keys(obj)) {
    if (!merged[domain]) {
      merged[domain] = {};
    }

    // Merge properties within domain
    merged[domain] = {
      ...merged[domain],
      ...obj[domain],
    };
  }

  return merged as FeatureFlags;
}
